import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { EVENT_STATUS, EVENT_CATEGORIES, catColor, frDate, MODULES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, MapPin, Users, Wallet, CheckCircle2, AlertTriangle, List, CalendarDays, Check, Download } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import CalendarGrid from "@/components/calendar/CalendarGrid";
import { CalendarToolbar, StatusCounters } from "@/components/calendar/CalendarToolbar";
import { rangeDays, rangeLabel, addDays } from "@/components/calendar/calendarUtils";
import EventsListView from "@/components/calendar/EventsListView";
import EventQuickSheet from "@/components/calendar/EventQuickSheet";

const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const navigate = useNavigate();
  const { org, user } = useAuth();
  const canMove = (user?.roles || []).some((r) => ["admin", "commercial", "chef_logistique"].includes(r));
  const orgModules = MODULES.filter((m) => (org?.modules || []).includes(m.key) && !(org?.access?.modules_soon || []).includes(m.key));
  const toggleModule = (k) => setForm((f) => ({ ...f, modules: (f.modules || []).includes(k) ? f.modules.filter((x) => x !== k) : [...(f.modules || []), k] }));
  const [events, setEvents] = useState([]);
  const eventsRef = React.useRef(events);
  eventsRef.current = events;
  const [contacts, setContacts] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("month");
  const [contactQuery, setContactQuery] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({ title: "", contact_id: "", venue_id: "", category: "Mariage", status: "prospect", date: ymd(new Date()), location: "", guests: "", budget: "", notes: "", modules: [] });
  const [venues, setVenues] = useState([]);
  const [venueQuery, setVenueQuery] = useState("");
  const [venueOpen, setVenueOpen] = useState(false);
  const [venueAddress, setVenueAddress] = useState("");
  const [venueCoords, setVenueCoords] = useState(null);
  const [activeCats, setActiveCats] = useState(() => new Set(EVENT_CATEGORIES));
  const toggleCat = (c) => setActiveCats((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const STATUS_KEYS = Object.keys(EVENT_STATUS);
  const [activeStatuses, setActiveStatuses] = useState(() => new Set(STATUS_KEYS));
  const toggleStatus = (s) => setActiveStatuses((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });

  const load = () => api.get("/events").then(({ data }) => setEvents(data));
  useEffect(() => { load(); api.get("/contacts").then(({ data }) => setContacts(data)); api.get("/venues").then(({ data }) => setVenues(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (searchParams.get("new")) { setOpen(true); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = () => { setActiveCats(new Set(EVENT_CATEGORIES)); setActiveStatuses(new Set(STATUS_KEYS)); };
  const pickStatus = (s) => setActiveStatuses((prev) => (prev.size === 1 && prev.has(s) ? new Set(STATUS_KEYS) : new Set([s])));
  const mode = view === "week" ? "week" : "month";
  const days = rangeDays(mode, cursor);
  const shift = (dir) => setCursor((c) => (mode === "week" ? addDays(c, 7 * dir) : new Date(c.getFullYear(), c.getMonth() + dir, 1)));

  const catOf = (e) => (EVENT_CATEGORIES.includes(e.category) ? e.category : "Autre");
  const catEvents = events.filter((e) => activeCats.has(catOf(e)));
  const visibleEvents = catEvents.filter((e) => activeStatuses.has(e.status || "prospect"));
  const inRange = (e) => { const d = (e.date || "").slice(0, 10); return view === "list" || (mode === "week" ? d >= ymd(days[0]) && d <= ymd(days[6]) : d.slice(0, 7) === ymd(cursor).slice(0, 7)); };
  const counts = catEvents.filter(inRange).reduce((a, e) => ({ ...a, [e.status || "prospect"]: (a[e.status || "prospect"] || 0) + 1 }), {});

  const moveEvent = async (id, ds, silent) => {
    const ev = eventsRef.current.find((x) => x.id === id);
    if (!ev || (ev.date || "").slice(0, 10) === ds) return;
    const from = (ev.date || "").slice(0, 10);
    setEvents((list) => list.map((x) => (x.id === id ? { ...x, date: ds } : x)));
    try {
      const { data } = await api.post(`/events/${id}/move`, { date: ds });
      setEvents((list) => list.map((x) => (x.id === id ? { ...x, ...data } : x)));
      if (!silent) toast.success(`« ${ev.title} » déplacé au ${new Date(ds + "T12:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`, { description: "Chargement, livraison et récupération décalés d'autant.", action: { label: "Annuler", onClick: () => moveEvent(id, from, true) } });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Déplacement impossible"); load(); }
  };

  const create = async () => {
    if (!form.title || !form.date) return toast.error("Titre et date requis");
    const willCreateContact = !form.contact_id && !!contactQuery.trim();
    try {
      let cid = form.contact_id;
      if (willCreateContact) {
        const { data: newC } = await api.post("/contacts", { name: contactQuery.trim(), email: contactEmail.trim(), phone: contactPhone.trim() });
        cid = newC.id;
        setContacts((prev) => [newC, ...prev]);
      }
      let vid = form.venue_id;
      let venueCreated = false;
      if (!vid && venueQuery.trim()) {
        const { data: newV } = await api.post("/venues", { name: venueQuery.trim(), address: venueAddress, address_coords: venueCoords || null, plans: [], photos: [] });
        vid = newV.id; venueCreated = true;
        setVenues((prev) => [newV, ...prev]);
      }
      const locName = form.location || venueQuery.trim() || venueAddress;
      await api.post("/events", { ...form, contact_id: cid || null, venue_id: vid || null, location: locName, location_coords: form.location_coords || venueCoords || null, guests: form.guests ? parseInt(form.guests) : null, budget: form.budget ? parseFloat(form.budget) : null, steps: [], modules: form.modules || [] });
      toast.success(venueCreated ? `Événement créé — lieu « ${venueQuery.trim()} » ajouté` : (willCreateContact ? `Événement créé — contact « ${contactQuery.trim()} » ajouté` : "Événement créé"));
      setOpen(false);
      setContactQuery(""); setContactEmail(""); setContactPhone("");
      setVenueQuery(""); setVenueAddress(""); setVenueCoords(null);
      setForm({ title: "", contact_id: "", venue_id: "", category: "Mariage", status: "prospect", date: ymd(new Date()), location: "", guests: "", budget: "", notes: "", modules: [] });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Erreur lors de la création"); }
  };

  const openEvent = async (id) => {
    const { data } = await api.get(`/events/${id}`);
    setSelected(data);
  };

  const exportCSV = () => {
    const rows = [...visibleEvents].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Titre", "Catégorie", "Statut", "Contact", "Lieu", "Invités", "Budget"];
    const lines = rows.map((e) => [e.date || "", e.title || "", e.category || "", (EVENT_STATUS[e.status] || {}).label || e.status || "", e.contact_name || "", e.location || "", e.guests ?? "", e.budget ?? ""].map(esc).join(","));
    const csv = "\ufeff" + [header.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `evenements_${ymd(new Date())}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} événement(s) exporté(s)`);
  };

  const completeEvent = async () => {
    try {
      await api.post(`/events/${selected.id}/complete`);
      toast.success("Événement clôturé — statut « Réalisé »");
      const { data } = await api.get(`/events/${selected.id}`);
      setSelected(data);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const cq = contactQuery.trim().toLowerCase();
  const contactMatches = cq ? contacts.filter((c) => (c.name || "").toLowerCase().includes(cq)).slice(0, 8) : [];
  const contactExact = contacts.some((c) => (c.name || "").toLowerCase() === cq);
  const vq = venueQuery.trim().toLowerCase();
  const venueMatches = vq ? venues.filter((v) => (v.name || "").toLowerCase().includes(vq)).slice(0, 8) : [];
  const venueExact = venues.some((v) => (v.name || "").toLowerCase() === vq);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Événements</h1>
          <p className="mt-1 text-sm text-zinc-500">Planifiez vos événements et suivez chargements, livraisons et récupérations.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setContactQuery(""); setContactEmail(""); setContactPhone(""); setVenueQuery(""); setVenueAddress(""); setVenueCoords(null); } }}>
          <DialogContent className="border-zinc-800 bg-zinc-950">
            <DialogHeader><DialogTitle className="text-white">Nouvel événement</DialogTitle><DialogDescription className="text-zinc-400">Renseignez le lieu, la date et les détails de l'événement.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-zinc-400">Titre *</Label><Input data-testid="event-title" className="mt-1 border-zinc-800 bg-zinc-900" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-400">Date *</Label><Input data-testid="event-date" type="date" className="mt-1 border-zinc-800 bg-zinc-900" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <Label className="text-zinc-400">Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1 border-zinc-800 bg-zinc-900"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900">{EVENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="relative">
                <Label className="text-zinc-400">Contact</Label>
                <Input
                  data-testid="event-contact-input"
                  className="mt-1 border-zinc-800 bg-zinc-900"
                  placeholder="Rechercher ou créer un contact…"
                  value={contactQuery}
                  onChange={(e) => { setContactQuery(e.target.value); setForm((f) => ({ ...f, contact_id: "" })); setContactOpen(true); }}
                  onFocus={() => setContactOpen(true)}
                  onBlur={() => setTimeout(() => setContactOpen(false), 150)}
                />
                {contactOpen && !!cq && (
                  <div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-900 p-1 shadow-xl" data-testid="event-contact-suggestions">
                    {contactMatches.map((c) => (
                      <button key={c.id} type="button" data-testid={`event-contact-opt-${c.id}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => { setForm((f) => ({ ...f, contact_id: c.id })); setContactQuery(c.name); setContactOpen(false); }}
                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800">
                        <span>{c.name}</span>{form.contact_id === c.id && <Check className="h-4 w-4 text-[#FF6A00]" />}
                      </button>
                    ))}
                    {!contactExact && (
                      <div className="px-3 py-2 text-xs text-[#FFD500]" data-testid="event-contact-new-hint">
                        Nouveau contact « {contactQuery.trim()} » — créé automatiquement à l'enregistrement
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!form.contact_id && !!cq && !contactExact && (
                <div className="grid grid-cols-2 gap-3 rounded-xl border border-[#FFD500]/25 bg-[#FFD500]/5 p-3" data-testid="event-new-contact-fields">
                  <div><Label className="text-xs text-zinc-400">Email (nouveau contact)</Label><Input data-testid="event-contact-email" type="email" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="email@exemple.fr" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></div>
                  <div><Label className="text-xs text-zinc-400">Téléphone</Label><Input data-testid="event-contact-phone" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="06 12 34 56 78" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></div>
                </div>
              )}
              <div className="relative">
                <Label className="text-zinc-400">Lieu de l'événement</Label>
                <Input
                  data-testid="event-venue-input"
                  className="mt-1 border-zinc-800 bg-zinc-900"
                  placeholder="Rechercher ou créer un lieu…"
                  value={venueQuery}
                  onChange={(e) => { setVenueQuery(e.target.value); setForm((f) => ({ ...f, venue_id: "", location: "", location_coords: null })); setVenueOpen(true); }}
                  onFocus={() => setVenueOpen(true)}
                  onBlur={() => setTimeout(() => setVenueOpen(false), 150)}
                />
                {venueOpen && !!vq && (
                  <div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-zinc-800 bg-zinc-900 p-1 shadow-xl" data-testid="event-venue-suggestions">
                    {venueMatches.map((v) => (
                      <button key={v.id} type="button" data-testid={`event-venue-opt-${v.id}`}
                        onMouseDown={(ev) => ev.preventDefault()}
                        onClick={() => { setForm((f) => ({ ...f, venue_id: v.id, location: v.name, location_coords: v.address_coords || null })); setVenueQuery(v.name); setVenueOpen(false); }}
                        className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800">
                        <span className="truncate">{v.name}</span>
                        <span className="truncate text-xs text-zinc-500">{v.address}</span>
                      </button>
                    ))}
                    {!venueExact && (
                      <div className="px-3 py-2 text-xs text-[#FFD500]" data-testid="event-venue-new-hint">
                        Nouveau lieu « {venueQuery.trim()} » — enregistré automatiquement
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!form.venue_id && !!vq && !venueExact && (
                <div className="rounded-xl border border-[#FFD500]/25 bg-[#FFD500]/5 p-3" data-testid="event-new-venue-address">
                  <Label className="text-xs text-zinc-400">Adresse du nouveau lieu</Label>
                  <AddressAutocomplete testid="event-venue-address" className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Salle, domaine, adresse…" value={venueAddress} onChange={(v) => setVenueAddress(v)} onSelect={(r) => setVenueCoords(r.coordinates || null)} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-400">Invités</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-900" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} /></div>
                <div><Label className="text-zinc-400">Budget (€)</Label><Input type="number" className="mt-1 border-zinc-800 bg-zinc-900" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
              </div>
              {orgModules.length > 0 && (
                <div data-testid="event-modules">
                  <Label className="text-zinc-400">Modules à activer</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {orgModules.map((m) => {
                      const on = (form.modules || []).includes(m.key);
                      const Icon = m.icon;
                      return (
                        <button key={m.key} type="button" data-testid={`event-module-${m.key}`} onClick={() => toggleModule(m.key)}
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-[#FF6A00] bg-[#FF6A00]/15 text-[#FF6A00]" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                          <Icon className="h-3.5 w-3.5" />{m.label}{on && <Check className="h-3.5 w-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button data-testid="save-event-btn" onClick={create} className="w-full rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900" data-testid="calendar-card">
        <CalendarToolbar label={rangeLabel(mode, cursor)} view={view} setView={setView}
          onPrev={() => shift(-1)} onNext={() => shift(1)} onToday={() => setCursor(new Date())}
          onExport={exportCSV} onNew={() => setOpen(true)}
          filters={{ activeCats, toggleCat, activeStatuses, toggleStatus, resetFilters }} />
        <StatusCounters counts={counts} activeStatuses={activeStatuses} onPick={pickStatus} />
        {view === "list" ? (
          <div className="border-t border-zinc-800 p-4"><EventsListView events={visibleEvents} onEventClick={(id) => navigate(`/app/evenements/${id}`)} /></div>
        ) : (
          <div className="border-t border-zinc-800">
            <CalendarGrid mode={mode} days={days} month={cursor.getMonth()} events={visibleEvents} logiEvents={catEvents}
              onEventClick={(id) => navigate(`/app/evenements/${id}`)}
              onMore={(dt) => { setCursor(dt); setView("week"); }}
              onMove={canMove ? moveEvent : undefined} />
          </div>
        )}
      </section>

      <EventQuickSheet selected={selected} onClose={() => setSelected(null)} onComplete={completeEvent}
        onCreateQuote={(id) => navigate(`/app/devis/nouveau?event=${id}`)} />
    </div>
  );
}
